"use client";
/** @jsxImportSource @emotion/react */
import { I18nMessage, useTranslations } from "@i18n/react";
import { useBranch } from "@packages/sdk";
import { ShareModal, Text, useModal } from "@packages/ui/emotion";
import { useCallback } from "react";

import { HallowAnalyticsEvent } from "../../../../../analytics/src";
import { Games } from "../../../lib/enums/games";
import { NativeMessages } from "../../../lib/enums/nativeMessages";
import { sendNativeMessage } from "../../../utils/sendNativeMessage";
import { Matrix } from "../components/matrix/Matrix";
import { DIFFICULTIES } from "../lib/constants/difficulties";
import { MATRIX_LENGTH } from "../lib/constants/matrix";
import { Colors } from "../lib/enums/colors";
import type { Game } from "../schema";

export const useShare = ({
  eventProperties = {},
  game,
  isSendNativeMessage,
}: {
  eventProperties?: any;
  game: Game;
  isSendNativeMessage: boolean;
}) => {
  const branch = useBranch();
  const modal = useModal(ShareModal);
  const t = useTranslations();

  const share = useCallback(async () => {
    if (isSendNativeMessage)
      sendNativeMessage({
        message: NativeMessages.DidTapShare,
        properties: {
          game,
          game_reference: Games.categories,
          ...eventProperties,
        },
      });
    else
      modal.show({
        children: (
          /** Next.js/Emotion css prop does not work */
          <div
            style={{
              alignItems: "center",
              backgroundColor: Colors.Primary,
              borderTopLeftRadius: 24,
              borderTopRightRadius: 24,
              color: Colors.Secondary,
              display: "flex",
              flexDirection: "column",
              gap: 8,
              paddingBottom: 24,
              paddingLeft: 8,
              paddingRight: 8,
              paddingTop: 16,
              textAlign: "center",
              width: "100%",
            }}
          >
            <Text type="title" size="l">
              <I18nMessage
                message="games_dynamic_on_hallow_puzzle"
                values={{
                  0: t("games_categories"),
                  1: game.game.id,
                }}
              />
              :
            </Text>
            <Matrix matrix={game.state.summary.matrix} />
          </div>
        ),
        color: Colors.Primary,
        imgSrc: game.game.images,
        link: await branch.categories({
          date: game.game.available_at,
        }),
        shareButtonAnalyticsEvent: {
          event: HallowAnalyticsEvent.TappedShareCategories,
          properties: eventProperties,
        },
        title: `${t("games_dynamic_on_hallow_puzzle", { 0: t("games_categories"), 1: game.game.id })}:${game.state.summary.matrix
          .slice(0, 8)
          .flat()
          .map(
            (entry, index) =>
              `${index % 4 === 0 ? "\n" : ""}${DIFFICULTIES[entry.difficulty].emoji}`,
          )
          .join("")}${
          game.state.summary.matrix.length > MATRIX_LENGTH
            ? `\n${t("games_categories_dynamic_guesses", {
                games_categories_dynamic_guesses:
                  game.state.summary.matrix.length - MATRIX_LENGTH,
              })}`
            : ""
        }`,
      });
  }, [branch, eventProperties, game, isSendNativeMessage, t]);

  return share;
};
